import {
  CleanPageDataResponse,
  CleanResponse,
  CombineDataType,
  GetProjectsResponse,
  UpdateProjectResponse
} from '@DStypes/project';
import apiRoutes from '@constants/apiRoutes';
import { setCurrentProjectData } from '@pages/ProjectBoard/slice';
import baseApi from '@services/api';
import store from '@store/store';
import { downloadBlob } from '@utils/common';

interface UploadDataFile {
  file_name: string;
  id?: string;
  sheet_name?: string;
  sheet_link?: string;
  sheet_id?: string;
  project_id?: string;
  xlsx_id?: string;
}

interface IGetSignedURLParams {
  file_name: string;
  size: number;
}
interface IGetSingedURLResponse {
  file_name: string;
  signed_url: string;
}
interface CombineFileParams {
  id: string;
  source_keyfield?: string;
  target_keyfield?: string;
}

const projects = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getProjects: builder.query<GetProjectsResponse[], null>({
      query: () => `${apiRoutes.projects}?is_deleted=False`,
      transformResponse: (response: GetProjectsResponse[]) =>
        response.sort(
          (a, b) =>
            Number(new Date(b.updated_at)) - Number(new Date(a.updated_at))
        ),

      providesTags: ['projectList']
    }),
    getDeletedProjects: builder.query<GetProjectsResponse[], void>({
      query: () => `${apiRoutes.projects}?is_deleted=True`,

      transformResponse: (response: GetProjectsResponse[]) =>
        response.sort(
          (a, b) =>
            Number(new Date(b.updated_at)) - Number(new Date(a.updated_at))
        ),
      providesTags: ['deletedProjectList']
    }),
    deleteProject: builder.mutation<any, string>({
      query: (id) => ({
        url: `${apiRoutes.projects}${id}/`,
        method: 'DELETE'
      }),
      invalidatesTags: ['projectList', 'deletedProjectList']
    }),
    uploadDataFile: builder.mutation<any, UploadDataFile>({
      query: ({
        file_name,
        sheet_name,
        sheet_link,
        sheet_id,
        project_id,
        xlsx_id
      }) => ({
        url: `${apiRoutes.data}/datafile/`,
        body: {
          file_name: file_name,
          project_id: project_id,
          metadata: {
            ...(sheet_name && { sheet_name: sheet_name }),
            ...(sheet_link && { sheet_link: sheet_link }),
            ...(sheet_id && { sheet_id: sheet_id }),
            ...(xlsx_id && { xlsx_id: xlsx_id })
          }
        },
        method: 'POST'
      }),
      invalidatesTags: ['projectList']
    }),
    getPresignedURL: builder.query<IGetSingedURLResponse, IGetSignedURLParams>({
      query: ({ file_name, size }) => ({
        url: `${apiRoutes.data}/url/?file_name=${encodeURIComponent(
          file_name
        )}&file_size=${size}`,
        method: 'GET'
      })
    }),
    getProjectFieldList: builder.query<any, any>({
      query: ({ projectId, fileId }) =>
        `${apiRoutes.data}/field/?project_id=${projectId}${
          fileId ? `&file_id=${fileId}` : ''
        }`,
      providesTags: ['projectFieldList']
    }),
    updateProjectFieldDetail: builder.mutation<any, any>({
      query: (payload) => ({
        url: `${apiRoutes.data}/field/${payload.id}/`,
        method: 'PUT',
        body: {
          ...{ to_import: payload.to_import },
          ...{ treat_as_non_sensitive: payload.treat_as_non_sensitive },
          ...(payload.treat_as_type && {
            treat_as_type: payload.treat_as_type
          }),
          ...(payload.fieldname && { fieldname: payload.fieldname })
        }
      })
    }),
    updateProject: builder.mutation<UpdateProjectResponse, any>({
      query: ({ id, payload }) => ({
        url: `${apiRoutes.projects}${id}/`,
        method: 'PUT',
        body: payload
      }),
      invalidatesTags: ['projectFieldList']
    }),
    restoreProject: builder.mutation<any, string>({
      query: (id) => ({
        url: `${apiRoutes.projects}${id}/restore/`,
        method: 'PUT'
      }),
      invalidatesTags: ['deletedProjectList']
    }),
    emptyTrash: builder.mutation<any, any>({
      query: () => ({
        url: `${apiRoutes.projects}trash/`,
        method: 'DELETE'
      }),
      invalidatesTags: ['deletedProjectList']
    }),
    acceptAndImportProject: builder.mutation<any, any>({
      query: ({ id, is_auto_clean }) => ({
        url: `${apiRoutes.data}/accept/?project_id=${id}&is_auto_clean=${is_auto_clean}`,
        method: 'POST'
      })
    }),
    getProjectById: builder.query<any, any>({
      query: ({ projectId: id, collaboration_id = '' }) => ({
        url: `${apiRoutes.projects}${id}/${
          collaboration_id ? `?collaboration_id=${collaboration_id}` : ''
        }`,
        method: 'GET'
      }),
      transformResponse: (data) => {
        store.dispatch(setCurrentProjectData(data));

        return data;
      }
    }),
    getAutoCharts: builder.mutation<UpdateProjectResponse, any>({
      query: ({ id, isAiDriven }) => ({
        url: `${apiRoutes.projects}${id}/auto/${
          isAiDriven ? '?is_ai_driven=True' : ''
        }`,
        method: 'PUT'
      })
    }),
    updateProjectChartOrder: builder.mutation<any, any>({
      query: ({ id, payload }) => ({
        url: `${apiRoutes.projects}${id}/reorder/`,
        method: 'PUT',
        body: { chart_id_order: payload }
      })
    }),
    createFeedBack: builder.mutation<any, any>({
      query: (payload) => ({
        url: `/feedback/`,
        method: 'POST',
        body: payload
      })
    }),
    getFeedbackTopicChoices: builder.query<any, any>({
      query: () => ({
        url: `/feedback/choices/`,
        method: 'GET'
      })
    }),
    getDataCleaningStatistics: builder.query<CleanResponse, any>({
      query: (id) => ({
        url: `/clean/${id}/`,
        method: 'GET'
      })
    }),
    updateProjectTheme: builder.mutation<
      any,
      { id: string; payload: { colorset: string | null } }
    >({
      query: ({ id, payload }) => ({
        url: `${apiRoutes.projects}${id}/theme/`,
        method: 'PUT',
        body: payload
      }),
      transformResponse: (data: { colorset: string[] }) => data.colorset
    }),
    getProjectLink: builder.query<string, string>({
      query: (id: string) => ({
        url: `/collab/project/link/?project_id=${id}`
      })
    }),
    updateEmailProjectLink: builder.mutation<any, any>({
      query: ({ id, emailIds }: { id: string; emailIds: string[] }) => ({
        url: `/collab/project/link/email/?project_id=${id}&email_ids=${emailIds}`,
        method: 'PUT'
      })
    }),
    createProjectComment: builder.mutation<any, any>({
      query: ({ id, payload }: any) => ({
        url: `collab/project/comment/?project_id=${id}`,
        method: 'POST',
        body: payload
      }),
      invalidatesTags: ['commentList']
    }),
    getProjectComments: builder.query<any, any>({
      query: (id) => `collab/project/comment/?project_id=${id}`,
      providesTags: ['commentList']
    }),
    generateProjectReport: builder.mutation<any, any>({
      query: ({ id, payload }) => ({
        url: `${apiRoutes.projects}${id}/reports/generate/`,
        method: 'PUT',
        body: payload
      })
    }),
    getDownloadProjectReport: builder.query<any, any>({
      query: ({ id, name }) => ({
        url: `${apiRoutes.projects}${id}/reports/download/?report_name=${name}`,
        method: 'GET',
        responseHandler: async (response: any) => ({
          blob: await response.blob(),
          filename: name
        }),
        cache: 'no-cache'
      }),
      transformResponse: (response: any) => {
        downloadBlob(response.blob, response.filename);

        return 'SUCCESS';
      }
    }),
    sendSquirrelBotRequest: builder.mutation<any, any>({
      query: (payload) => ({
        url: `${apiRoutes.projects}prompt-processing/`,
        method: 'PUT',
        body: payload
      })
    }),
    generateCleanedDataFile: builder.mutation<any, any>({
      query: ({ id, payload }) => ({
        url: `${apiRoutes.projects}${id}/clean/file/generate/`,
        method: 'PUT',
        body: payload
      })
    }),
    getDownloadCleanedFile: builder.query<any, any>({
      query: ({ id, name }) => ({
        url: `${
          apiRoutes.projects
        }${id}/clean/file/download/?file_name=${encodeURIComponent(name)}`,
        method: 'GET',
        responseHandler: async (response: any) => ({
          blob: await response.blob(),
          filename: name
        }),
        cache: 'no-cache'
      }),
      transformResponse: (response: any) => {
        downloadBlob(response.blob, response.filename);

        return 'SUCCESS';
      }
    }),
    getCombineFilesDetails: builder.query<CombineDataType, CombineFileParams>({
      query: ({ id, source_keyfield, target_keyfield }) => ({
        url: source_keyfield
          ? `/data/combine/?file_id=${id}&source_keyfield=${source_keyfield}&target_keyfield=${target_keyfield}`
          : `/data/combine/?file_id=${id}`
      })
    }),
    combineDataFiles: builder.mutation<any, any>({
      query: (payload: any) => ({
        url: `data/file/combine/`,
        method: 'POST',
        body: payload
      })
    }),
    cancelCombine: builder.mutation<any, any>({
      query: ({
        file_id,
        project_id
      }: {
        file_id: string;
        project_id: string;
      }) => ({
        url: `data/file/combine/cancel/?file_id=${file_id}&project_id=${project_id}`,
        method: 'POST'
      })
    }),
    getColumnValues: builder.query<any, any>({
      query: ({
        project_id,
        field_id,
        date_group
      }: {
        project_id: string;
        field_id: string;
        date_group?: string;
      }) => ({
        url: `project/${project_id}/field/values/?field_id=${field_id}&${
          date_group ? `date_group=${date_group}` : ''
        }`,
        method: 'GET'
      })
    }),
    updateProjectBoardFilters: builder.mutation<any, any>({
      query: ({
        project_id,
        payload
      }: {
        project_id: string;
        payload: any;
      }) => ({
        url: `project/${project_id}/filters/`,
        body: payload,
        method: 'PUT'
      })
    }),
    updateDynamicFilters: builder.mutation<any, any>({
      query: ({
        project_id,
        payload
      }: {
        project_id: string;
        payload: any;
      }) => ({
        url: `project/${project_id}/dynamic_filters/`,
        body: payload,
        method: 'PUT'
      })
    }),
    getCleanPageData: builder.query<CleanPageDataResponse, any>({
      query: ({ project_id, type }: { project_id: string; type: string }) => ({
        url: `clean/${project_id}/dirt-clean-options/?listed_by=${type}`,
        method: 'GET'
      })
    }),
    updateCleanAction: builder.mutation<any, any>({
      query: ({
        project_id,
        payload
      }: {
        project_id: string;
        payload: any;
      }) => ({
        url: `clean/${project_id}/dirt-clean-action/`,
        body: payload,
        method: 'PUT'
      })
    }),
    refreshSpreadsheetData: builder.mutation<any, any>({
      query: ({ project_id }: { project_id: string }) => ({
        url: `project/${project_id}/cloud/update/`,
        method: 'PUT'
      })
    }),
    convertToGsheet: builder.mutation<any, any>({
      query: ({ file_id }: { file_id: string }) => ({
        url: `data/convert/?file_id=${file_id}`,
        method: 'POST'
      })
    })
  })
});

export const {
  useGetProjectsQuery,
  useLazyGetDeletedProjectsQuery,
  useDeleteProjectMutation,
  useUploadDataFileMutation,
  useGetProjectFieldListQuery,
  useUpdateProjectFieldDetailMutation,
  useUpdateProjectMutation,
  useGetAutoChartsMutation,
  useRestoreProjectMutation,
  useEmptyTrashMutation,
  useAcceptAndImportProjectMutation,
  useGetProjectByIdQuery,
  useCreateFeedBackMutation,
  useGetFeedbackTopicChoicesQuery,
  useLazyGetDataCleaningStatisticsQuery,
  useUpdateProjectThemeMutation,
  useLazyGetProjectLinkQuery,
  useUpdateEmailProjectLinkMutation,
  useCreateProjectCommentMutation,
  useGetProjectCommentsQuery,
  useLazyGetProjectCommentsQuery,
  useUpdateProjectChartOrderMutation,
  useGenerateProjectReportMutation,
  useSendSquirrelBotRequestMutation,
  useLazyGetDownloadProjectReportQuery,
  useLazyGetProjectByIdQuery,
  useLazyGetDownloadCleanedFileQuery,
  useGenerateCleanedDataFileMutation,
  useLazyGetCombineFilesDetailsQuery,
  useCombineDataFilesMutation,
  useCancelCombineMutation,
  useLazyGetColumnValuesQuery,
  useUpdateProjectBoardFiltersMutation,
  useUpdateDynamicFiltersMutation,
  useLazyGetPresignedURLQuery,
  useLazyGetCleanPageDataQuery,
  useUpdateCleanActionMutation,
  useRefreshSpreadsheetDataMutation,
  useConvertToGsheetMutation
} = projects;
